import React, { useEffect, useRef } from "react";
import { useDrawLoop } from "./utils";
import "./styles.css";

export const handler = ({ inputs, mechanic }) => {
  const {
    width,
    height,
    backgroundColor,
    primaryColor,
    secondaryColor,
    title,
    subtitle,
    fontSize,
    duration
  } = inputs;

  const startTime = useRef(Date.now());
  const isPlaying = useRef(true);
  const frameCount = useDrawLoop(isPlaying.current);

  useEffect(() => {
    if (Date.now() - startTime.current < duration * 1000) {
      mechanic.frame();
    } else if (isPlaying.current) {
      isPlaying.current = false;
      mechanic.done();
    }
  }, [frameCount]);

  const elapsed = Date.now() - startTime.current;
  const progress = Math.min(elapsed / (duration * 1000), 1);

  return (
    <div
      className="html-container"
      style={{
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor: backgroundColor,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "40px",
        boxSizing: "border-box"
      }}
    >
      <h1
        style={{
          color: primaryColor,
          fontSize: `${fontSize}px`,
          fontWeight: "bold",
          marginBottom: "20px",
          textAlign: "center",
          fontFamily: "Arial, sans-serif"
        }}
      >
        {title}
      </h1>
      
      <p
        style={{
          color: secondaryColor,
          fontSize: `${fontSize * 0.5}px`,
          textAlign: "center",
          fontFamily: "Arial, sans-serif",
          maxWidth: "80%"
        }}
      >
        {subtitle}
      </p>

      <div
        className="decorative-boxes"
        style={{
          display: "flex",
          gap: "20px",
          marginTop: "40px"
        }}
      >
        {[1, 2, 3].map((num) => {
          const rotation = progress * 360 * num;
          const scale = 1 + Math.sin(progress * Math.PI * 2 * num) * 0.3;
          const translateY = Math.sin(progress * Math.PI * 2 + num) * 20;
          
          return (
            <div
              key={num}
              className="box"
              style={{
                width: "80px",
                height: "80px",
                backgroundColor: num % 2 === 0 ? primaryColor : secondaryColor,
                borderRadius: "8px",
                transform: `rotate(${rotation}deg) scale(${scale}) translateY(${translateY}px)`
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

export const inputs = {
  width: {
    type: "number",
    default: 800
  },
  height: {
    type: "number",
    default: 600
  },
  backgroundColor: {
    type: "color",
    model: "hex",
    default: "#ffffff"
  },
  primaryColor: {
    type: "color",
    model: "hex",
    default: "#6691cb"
  },
  secondaryColor: {
    type: "color",
    model: "hex",
    default: "#fcf3c2"
  },
  title: {
    type: "text",
    default: "HTML Design"
  },
  subtitle: {
    type: "text",
    default: "This design uses HTML elements instead of SVG"
  },
  fontSize: {
    type: "number",
    default: 48,
    min: 20,
    max: 120,
    slider: true,
    label: "Title Font Size"
  },
  duration: {
    type: "number",
    default: 5,
    min: 1,
    max: 20,
    slider: true,
    label: "Duration (seconds)"
  }
};

export const presets = {
  bold: {
    primaryColor: "#000000",
    secondaryColor: "#ff0000",
    backgroundColor: "#f5f5f5"
  },
  pastel: {
    primaryColor: "#a8dadc",
    secondaryColor: "#f1faee",
    backgroundColor: "#457b9d"
  }
};

export const settings = {
  engine: require("@mechanic-design/engine-react"),
  name: "HTML Design",
  animated: true,
  hideFunction: true
};
